import * as React from "react";
import {
  Form,
  Link,
  isRouteErrorResponse,
  redirect,
  useActionData,
  useLoaderData,
  useNavigation,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from "react-router";

import { ArrowRight, BookOpen, Check, Loader2, LogOut, Save, User, Video } from "lucide-react";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { cn, youtubeId } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { AuthHeader, INPUT_CLASS } from "@/pages/Login";

import { PAGE_COUNT, type Video as VideoRow } from "@/lib/types";

function parsePage(value: unknown) {
  const page = Number(value ?? 1);
  return Number.isInteger(page) && page >= 1 && page <= PAGE_COUNT ? page : 1;
}

/** Status carried by an API error (a thrown `data()` or a route error response). */
function errorStatus(err: unknown) {
  if (isRouteErrorResponse(err)) return err.status;
  const init = (err as { init?: { status?: number } | null } | null)?.init;
  return init?.status;
}

export async function adminLoader({ request }: LoaderFunctionArgs) {
  const pageNumber = parsePage(new URL(request.url).searchParams.get("page"));
  // A page without a Video row shows an empty state instead of the error page.
  const video = await api<VideoRow>(`/api/videos/${pageNumber}`).catch((err) => {
    if (errorStatus(err) === 404) return null;
    throw err;
  });
  return { video, pageNumber };
}

export async function adminAction({ request }: ActionFunctionArgs) {
  const objData = Object.fromEntries(await request.formData());

  if (objData.formType === "PAGE_SEARCH_FORM") {
    return redirect(`/admin?page=${parsePage(objData.pageNumber)}`);
  }

  try {
    const video = await api<VideoRow>(`/api/videos/${parsePage(objData.id)}`, {
      method: "PUT",
      body: JSON.stringify({
        tajweed_youtube_url: objData.tajweed_youtube_url,
        hifz_youtube_url: objData.hifz_youtube_url,
      }),
    });
    return { video, error: null };
  } catch (err) {
    if (err instanceof Response) throw err; // redirect to /login
    // 400s carry the API's Bosnian validation message ("Neispravan hifz YouTube link").
    const message =
      errorStatus(err) === 400
        ? String((err as { data?: unknown }).data)
        : "Spremanje nije uspjelo";
    return { video: null, error: message };
  }
}

type AdminLoaderData = Awaited<ReturnType<typeof adminLoader>>;
type AdminActionData = Exclude<Awaited<ReturnType<typeof adminAction>>, Response>;


const CARD = "rounded-card bg-paper-50 shadow-card ring-1 ring-paper-300";

const VideoUpdate: React.FC = () => {
  const loaderData = useLoaderData<AdminLoaderData>();
  const { data: sessionData, isPending: sessionPending } = authClient.useSession();
  const pageInputId = React.useId();
  const videoTitleId = React.useId();
  const userName = sessionData?.user.name;

  return (
    <div className="min-h-svh bg-paper-50">
      <AuthHeader>
        <div className="flex min-w-0 items-center gap-1.5">
          {(userName || sessionPending) && (
            <div className="flex h-9 min-w-0 items-center gap-2 rounded-full bg-paper-200/70 px-3 text-sm font-medium text-ink">
              <User aria-hidden className="size-4 shrink-0 text-ink-muted" />
              {userName ? (
                <span className="max-w-[7.5rem] truncate sm:max-w-[16rem]">{userName}</span>
              ) : (
                <span
                  aria-hidden
                  className="h-3 w-16 rounded-full bg-paper-300/80 motion-safe:animate-pulse"
                />
              )}
            </div>
          )}
          <button
            type="button"
            aria-label="Odjava"
            title="Odjava"
            className="pressable grid size-10 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-paper-200 hover:text-ink"
            onClick={async () => {
              await authClient.signOut();
              window.location.href = "/";
            }}
          >
            <LogOut aria-hidden className="size-4" />
          </button>
        </div>
      </AuthHeader>

      <main className="mx-auto max-w-3xl px-4 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          Upravljanje video snimcima
        </h1>
        <p className="mt-1 text-ink-muted">
          Uredi video linkove za hifz i tedžvid objašnjenja
        </p>

        {/* Page picker */}
        <Form method="post" className={cn(CARD, "mt-8 p-5")}>
          <input type="hidden" name="formType" value="PAGE_SEARCH_FORM" />
          <h2 className="text-sm font-semibold text-ink">
            <label htmlFor={pageInputId}>Pronađi stranicu</label>
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 sm:max-w-44">
              <input
                key={loaderData.pageNumber}
                id={pageInputId}
                type="number"
                inputMode="numeric"
                enterKeyHint="go"
                defaultValue={loaderData.pageNumber}
                min={1}
                max={PAGE_COUNT}
                required
                name="pageNumber"
                className={cn(
                  INPUT_CLASS,
                  "pr-16 font-medium tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                )}
                placeholder="Unesi broj stranice"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-sm text-ink-subtle">
                od {PAGE_COUNT}
              </span>
            </div>
            <Button type="submit" size="lg">
              Pretraga
            </Button>
            <Button variant="ghost" size="lg" asChild className="w-full px-4 sm:w-auto">
              <Link to={`/${loaderData.pageNumber}`}>
                Otvori stranicu
                <ArrowRight aria-hidden />
              </Link>
            </Button>
          </div>
        </Form>

        {/* Video form */}
        <section aria-labelledby={videoTitleId} className={cn(CARD, "mt-6 overflow-hidden")}>
          <div className="border-b border-paper-300/70 bg-paper-100 px-5 py-4">
            <h2 id={videoTitleId} className="text-[15px] font-semibold text-ink">
              Stranica {loaderData.pageNumber}
            </h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              Podesi URL-ove videa za ovu stranicu
            </p>
          </div>
          <div className="p-5">
            {loaderData.video ? (
              <VideoUpdateForm key={loaderData.pageNumber} video={loaderData.video} />
            ) : (
              <p className="rounded-card bg-paper-100 px-4 py-6 text-center text-sm text-ink-muted ring-1 ring-paper-300">
                Za ovu stranicu još nema video zapisa.
              </p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

function VideoField({
  name,
  label,
  linkLabel,
  defaultValue,
  icon,
  onEdit,
}: {
  name: "hifz_youtube_url" | "tajweed_youtube_url";
  label: string;
  /** Accessible name of the thumbnail link. */
  linkLabel: string;
  defaultValue: string;
  icon: React.ReactNode;
  onEdit: () => void;
}) {
  const inputId = React.useId();
  const [value, setValue] = React.useState(defaultValue);
  const id = youtubeId(value);
  // Thumbnail that failed to load (offline, blocked): keep the slot, show the icon.
  const [failedId, setFailedId] = React.useState<string | null>(null);

  return (
    <div>
      <label htmlFor={inputId} className="flex items-center gap-2.5 text-sm font-medium text-ink">
        {icon}
        {label}
      </label>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="text"
          id={inputId}
          name={name}
          autoComplete="off"
          spellCheck={false}
          className={cn(INPUT_CLASS, "min-w-0 flex-1")}
          defaultValue={defaultValue}
          onChange={(e) => {
            setValue(e.target.value);
            onEdit();
          }}
          placeholder="YouTube link ili ID videa"
        />
        {/* Thumbnail confirms the id; the slot keeps its size so the input never resizes. */}
        {id ? (
          <a
            href={`https://www.youtube.com/watch?v=${id}`}
            target="_blank"
            rel="noreferrer"
            aria-label={linkLabel}
            className="block h-[54px] w-24 shrink-0 overflow-hidden rounded-md bg-paper-200 ring-1 ring-paper-300 transition-shadow duration-150 hover:ring-gold-400"
          >
            {failedId === id ? (
              <span className="grid size-full place-items-center text-ink-subtle">
                <Video aria-hidden className="size-4" />
              </span>
            ) : (
              <img
                src={`https://i.ytimg.com/vi/${id}/mqdefault.jpg`}
                alt=""
                width={96}
                height={54}
                loading="lazy"
                decoding="async"
                onError={() => setFailedId(id)}
                className="size-full object-cover"
              />
            )}
          </a>
        ) : (
          <span
            aria-hidden
            className="grid h-[54px] w-24 shrink-0 place-items-center rounded-md bg-paper-100 text-ink-subtle/60 ring-1 ring-paper-300"
          >
            <Video className="size-4" />
          </span>
        )}
      </div>
    </div>
  );
}

function VideoUpdateForm({ video }: { video: VideoRow }) {
  const loaderData = useLoaderData<AdminLoaderData>();
  const actionData = useActionData<AdminActionData>();
  const navigation = useNavigation();
  // Hide the last result once the admin edits a field again.
  const [edited, setEdited] = React.useState(false);
  const onEdit = React.useCallback(() => setEdited(true), []);

  // Submitting, then revalidating the loader with the saved row.
  const saving =
    navigation.state !== "idle" &&
    navigation.formData?.get("formType") === "VIDEO_UPDATE_FORM";
  const saved =
    !edited && !saving && actionData?.video?.id === loaderData.pageNumber;
  const error = !edited && !saving ? actionData?.error : null;

  return (
    <Form method="post" onSubmit={() => setEdited(false)}>
      <input type="hidden" name="id" value={video.id} />
      <input type="hidden" name="formType" value="VIDEO_UPDATE_FORM" />

      <div className="space-y-6">
        <VideoField
          name="hifz_youtube_url"
          label="URL za hifz video"
          linkLabel="Otvori hifz video na YouTubeu"
          defaultValue={video.hifz_youtube_url || ""}
          onEdit={onEdit}
          icon={
            <span className="grid size-7 shrink-0 place-items-center rounded-md bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100">
              <BookOpen aria-hidden className="size-3.5" />
            </span>
          }
        />
        <VideoField
          name="tajweed_youtube_url"
          label="URL za tedžvid video"
          linkLabel="Otvori tedžvid video na YouTubeu"
          defaultValue={video.tajweed_youtube_url || ""}
          onEdit={onEdit}
          icon={
            <span className="grid size-7 shrink-0 place-items-center rounded-md bg-gold-100 text-gold-700 ring-1 ring-gold-200">
              <Video aria-hidden className="size-3.5" />
            </span>
          }
        />
      </div>

      <div className="mt-6 flex flex-col gap-3 border-t border-paper-300/70 pt-5 sm:flex-row sm:items-center sm:gap-4">
        <Button type="submit" size="lg" disabled={saving} className="w-full disabled:opacity-80 sm:w-auto">
          {saving ? <Loader2 aria-hidden className="animate-spin" /> : <Save aria-hidden />}
          {saving ? "Spremanje..." : "Spremi izmjene"}
        </Button>
        <div className="min-w-0 text-sm">
          {/* Always mounted, so the success message is announced when it appears. */}
          <p role="status">
            {saved && (
              <span className="inline-flex items-center gap-1.5 font-medium text-emerald-800">
                <Check aria-hidden className="size-4 shrink-0" />
                Izmjene uspješno sačuvane
              </span>
            )}
          </p>
          {error && (
            <p role="alert" className="text-rose-700">
              {error}
            </p>
          )}
        </div>
      </div>
    </Form>
  );
}

export default VideoUpdate;
